/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Resolve SQL compilation errors", description = "Troubleshoot SQL compilation errors that appear in model previews, Customer Studio audiences and journeys, or sync runs.", lastUpdatedDate = new Date(1770681600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Resolve SQL compilation errors",
        description: "Troubleshoot SQL compilation errors that appear in model previews, Customer Studio audiences and journeys, or sync runs.",
        lastUpdatedDate: "Tue Feb 10 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Where these errors appear",
          "depth": 0,
          "id": "where-these-errors-appear"
        }, {
          "value": "Quick checklist",
          "depth": 0,
          "id": "quick-checklist"
        }, {
          "value": "Common error patterns",
          "depth": 0,
          "id": "common-error-patterns"
        }, {
          "value": "Invalid identifier (including _ht_to_filter)",
          "depth": 1,
          "id": "invalid-identifier-including-_ht_to_filter"
        }, {
          "value": "Unresolved column with suggestions",
          "depth": 1,
          "id": "unresolved-column-with-suggestions"
        }, {
          "value": "Ambiguous column name",
          "depth": 1,
          "id": "ambiguous-column-name"
        }, {
          "value": "Object does not exist or not authorized",
          "depth": 1,
          "id": "object-does-not-exist-or-not-authorized"
        }, {
          "value": "View definition column mismatch",
          "depth": 1,
          "id": "view-definition-column-mismatch"
        }, {
          "value": "Invalid data type for predicate / row comparison",
          "depth": 1,
          "id": "invalid-data-type-for-predicate--row-comparison"
        }, {
          "value": "Works in warehouse UI but fails in Hightouch",
          "depth": 1,
          "id": "works-in-warehouse-ui-but-fails-in-hightouch"
        }, {
          "value": "When to contact support",
          "depth": 0,
          "id": "when-to-contact-support"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "SQL compilation errors in Hightouch usually come from your warehouse when Hightouch runs a query to preview a model, compute an audience, evaluate journey logic, or execute a sync."
      }), "\n", _jsx(_components.p, {
        children: "In most cases, Hightouch is surfacing the warehouse's error message directly."
      }), "\n", _jsx(_components.h2, {
        id: "where-these-errors-appear",
        children: "Where these errors appear"
      }), "\n", _jsx(_components.p, {
        children: "You may see SQL compilation errors in:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Model preview and model edits"
        }), "\n", _jsx(_components.li, {
          children: "Customer Studio audience preview or count calculations"
        }), "\n", _jsx(_components.li, {
          children: "Customer Studio journeys that evaluate audience/event conditions"
        }), "\n", _jsx(_components.li, {
          children: "Sync run failures"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "quick-checklist",
        children: "Quick checklist"
      }), "\n", _jsx(_components.p, {
        children: "When you hit a SQL compilation error:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Copy the full error message and note object names, column names, and line/position details."
        }), "\n", _jsx(_components.li, {
          children: "Run the same SQL in your warehouse using the same role/context as Hightouch."
        }), "\n", _jsx(_components.li, {
          children: "Classify the error pattern below."
        }), "\n", _jsx(_components.li, {
          children: "Fix the issue at the right layer (warehouse object/permissions vs model/filter/mapping config)."
        }), "\n", _jsx(_components.li, {
          children: "Re-run model preview, then re-run the audience/journey/sync flow."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "common-error-patterns",
        children: "Common error patterns"
      }), "\n", _jsxs(_components.h3, {
        id: "invalid-identifier-including-_ht_to_filter",
        children: ["Invalid identifier (including ", _jsx(_components.code, {
          children: "_ht_to_filter"
        }), ")"]
      }), "\n", _jsx(_components.p, {
        children: "Example patterns:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "SQL compilation error: invalid identifier ..."
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "invalid identifier '\"_ht_to_filter\".\"COLUMN_NAME\"'"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What it means:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A referenced column is missing from the query result Hightouch is filtering or mapping."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Column renamed or removed in model SQL."
        }), "\n", _jsx(_components.li, {
          children: "Audience filter, trait, journey condition, or sync mapping still references old column."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Confirm the column still exists in model output."
        }), "\n", _jsx(_components.li, {
          children: "Update filters/conditions/mappings that reference the old name."
        }), "\n", _jsx(_components.li, {
          children: "Preview and re-save the model if metadata is stale."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "unresolved-column-with-suggestions",
        children: "Unresolved column with suggestions"
      }), "\n", _jsx(_components.p, {
        children: "Example pattern:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "[UNRESOLVED_COLUMN.WITH_SUGGESTION] ... cannot be resolved. Did you mean ..."
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What it means:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A referenced column name does not exist in the active query output."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Typo in a field name."
        }), "\n", _jsxs(_components.li, {
          children: ["Renamed/removed model column (for example after changing from ", _jsx(_components.code, {
            children: "SELECT *"
          }), " to an explicit column list)."]
        }), "\n", _jsx(_components.li, {
          children: "Stale audience, trait, journey filter, or suggestion-job configuration."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Verify the column exists in the parent model/event model output."
        }), "\n", _jsx(_components.li, {
          children: "Preview and re-save the model to refresh available columns."
        }), "\n", _jsx(_components.li, {
          children: "Update filter logic to use valid column names."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "ambiguous-column-name",
        children: "Ambiguous column name"
      }), "\n", _jsx(_components.p, {
        children: "Example pattern:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "SQL compilation error: ambiguous column name 'COLUMN_NAME'"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What it means:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The final query references a column name that exists in multiple joined sources, and the warehouse cannot infer which one you intended."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Common causes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Multiple tables expose the same column name and the model output does not disambiguate with aliases."
        }), "\n", _jsx(_components.li, {
          children: "Downstream filters/mappings reference a shared name after query wrapping."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Alias ambiguous columns in model SQL (for example, ", _jsx(_components.code, {
            children: "a.faq_url AS faq_url_from_a"
          }), ", ", _jsx(_components.code, {
            children: "b.faq_url AS faq_url_from_b"
          }), ")."]
        }), "\n", _jsx(_components.li, {
          children: "Update filters, traits, journeys, and sync mappings to reference the intended alias."
        }), "\n", _jsx(_components.li, {
          children: "Re-run preview and dependent workflows."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "object-does-not-exist-or-not-authorized",
        children: "Object does not exist or not authorized"
      }), "\n", _jsx(_components.p, {
        children: "Example pattern:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Object 'DB.SCHEMA.OBJECT' does not exist or not authorized"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What it means:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Warehouse object is missing, moved, renamed, or inaccessible to the configured role."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Verify object path (database/schema/object) is correct."
        }), "\n", _jsxs(_components.li, {
          children: ["Confirm the Hightouch role has required ", _jsx(_components.code, {
            children: "USAGE"
          }), "/", _jsx(_components.code, {
            children: "SELECT"
          }), " privileges."]
        }), "\n", _jsx(_components.li, {
          children: "Update model SQL or restore the object."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "view-definition-column-mismatch",
        children: "View definition column mismatch"
      }), "\n", _jsx(_components.p, {
        children: "Example pattern:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "View definition ... declared N column(s), but view query produces M column(s)"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What it means:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The warehouse view definition is out of sync with the underlying query."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Recreate or repair the view in the warehouse."
        }), "\n", _jsxs(_components.li, {
          children: ["Prefer explicit column lists over ", _jsx(_components.code, {
            children: "SELECT *"
          }), " in long-lived views."]
        }), "\n", _jsx(_components.li, {
          children: "Re-test the view in warehouse SQL before re-running in Hightouch."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "invalid-data-type-for-predicate--row-comparison",
        children: "Invalid data type for predicate / row comparison"
      }), "\n", _jsx(_components.p, {
        children: "Example pattern:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Invalid data type ... for predicate [ROW(...)]"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "What it means:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Filter predicates compare incompatible data types."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Inspect predicate/filter logic for mixed types."
        }), "\n", _jsxs(_components.li, {
          children: ["Align types with explicit ", _jsx(_components.code, {
            children: "CAST(...)"
          }), " where needed."]
        }), "\n", _jsx(_components.li, {
          children: "Re-test in warehouse, then in Hightouch."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "works-in-warehouse-ui-but-fails-in-hightouch",
        children: "Works in warehouse UI but fails in Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "What to check:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Role differences between your user and Hightouch connection."
        }), "\n", _jsx(_components.li, {
          children: "Different default database/schema/warehouse context."
        }), "\n", _jsx(_components.li, {
          children: "Model points to a different object/version than expected."
        }), "\n", _jsx(_components.li, {
          children: "SQL succeeds in UI context but fails under Hightouch role with a standard pattern above."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "How to fix:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Re-run under the same role/context as Hightouch."
        }), "\n", _jsxs(_components.li, {
          children: ["Use fully qualified names (", _jsx(_components.code, {
            children: "DATABASE.SCHEMA.OBJECT"
          }), ") in SQL."]
        }), "\n", _jsx(_components.li, {
          children: "Confirm Hightouch model references the intended object."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "when-to-contact-support",
        children: "When to contact support"
      }), "\n", _jsx(_components.p, {
        children: "Contact support with:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Link to model/audience/journey/sync run"
        }), "\n", _jsx(_components.li, {
          children: "Full SQL compilation error text"
        }), "\n", _jsx(_components.li, {
          children: "Whether the error reproduces in warehouse SQL with the same role/context"
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
